import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import api from "../../services/api";
import Layout from "../common/Layout";
import ScheduleExportModal from "./ScheduleExportModal";
import ReportTemplatesPanel from "../reports/templates/ReportTemplatesPanel";
import ReportTemplateViewPanel from "../reports/templates/ReportTemplateViewPanel";
import { formatInUserTimezone } from "../../utils/timezone";
import { COLUMN_PRESETS } from "./ColumnPresets";

const STATUS_LABEL = {
  active: "Active",
  held: "On hold",
  cancelled: "Cancelled",
};

const STATUS_BADGE_CLASS = {
  active: "active",
  held: "held",
  cancelled: "cancelled",
};

const RANGE_LABEL = {
  all: "All time",
  last_7: "Last 7 d",
  last_14: "Last 14 d",
  last_30: "Last 30 d",
  this_month: "This month",
  this_quarter: "This Q",
  last_quarter: "Last Q",
  last_6_months: "Last 6 mo",
  this_year: "This year",
  last_year: "Last year",
  ytd: "YTD",
  custom: "Custom",
};

function extractFilename(contentDisposition, fallback) {
  if (!contentDisposition) return fallback;
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(contentDisposition);
  return m ? decodeURIComponent(m[1].replace(/"/g, "")) : fallback;
}

function triggerBlobDownload(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

export default function ScheduledExportsPanel() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focusId = params.get("focus");

  const [viewMode, setViewMode] = useState("reports"); // "reports" | "templates"
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateRefreshKey, setTemplateRefreshKey] = useState(0);

  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalEditRow, setModalEditRow] = useState(null);

  const [popover, setPopover] = useState(null);
  const [delayDays, setDelayDays] = useState(1);
  const popoverRef = useRef(null);

  const [panelAction, setPanelAction] = useState(null);
  const [panelDelayDays, setPanelDelayDays] = useState(1);

  const [busyRowId, setBusyRowId] = useState(null);

  const isOnDemand = (row) => row?.frequency === "on_demand";

  /* On-demand reports without recipients cannot be emailed. */
  const canEmailNow = (row) => {
    if (!row) return false;
    if (!isOnDemand(row)) return true;
    return Array.isArray(row.recipients) && row.recipients.length > 0;
  };

  const openRow = useCallback(async (row) => {
    setSelected(row);
    setPanelAction(null);
    setPopover(null);
    try {
      const res = await api.getScheduledExportRuns(row.id);
      setRuns(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setRuns([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.getScheduledExports();
      const data = Array.isArray(res?.data) ? res.data : [];
      setRows(data);
      if (focusId) {
        const wanted = data.find((r) => String(r.id) === String(focusId));
        if (wanted) {
          await openRow(wanted);
          return;
        }
      }
      setSelected((prev) => {
        if (!prev) return null;
        return data.find((r) => r.id === prev.id) || null;
      });
    } catch {
      setError("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [focusId, openRow]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  useEffect(() => {
    if (!popover) return;
    const onDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setPopover(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [popover]);

  const showFlash = (msg) => {
    setFlash(msg);
    if (showFlash._t) clearTimeout(showFlash._t);
    showFlash._t = setTimeout(() => setFlash(""), 3000);
  };

  const refreshSelected = async (id) => {
    const fresh = (await api.getScheduledExports())?.data?.find(
      (r) => r.id === id,
    );
    if (fresh) {
      setSelected(fresh);
      try {
        const res = await api.getScheduledExportRuns(fresh.id);
        setRuns(Array.isArray(res?.data) ? res.data : []);
      } catch {
        setRuns([]);
      }
    }
  };

  const runAction = async (fn, row, successMsg) => {
    try {
      await fn(row);
      showFlash(successMsg || "Done.");
      await load();
      if (selected?.id === row.id) await refreshSelected(row.id);
    } catch {
      setError("Action failed.");
    }
  };

  const handleDownload = async (row) => {
    setBusyRowId(row.id);
    try {
      const { blob, contentDisposition } = await api.downloadScheduledExport(
        row.id,
      );
      const fallback = `${(row.name || "report").replace(/\s+/g, "_")}.${row.format || "xlsx"}`;
      const fileName = extractFilename(contentDisposition, fallback);
      triggerBlobDownload(blob, fileName);
      showFlash(`Downloaded "${fileName}".`);
      if (selected?.id === row.id) await refreshSelected(row.id);
    } catch {
      setError("Download failed.");
    } finally {
      setBusyRowId(null);
    }
  };

  const handleEmailNow = async (row) => {
    if (!canEmailNow(row)) {
      showFlash("Add at least one recipient before emailing.");
      return;
    }
    setBusyRowId(row.id);
    try {
      await api.emailScheduledExport(row.id);
      const count = Array.isArray(row.recipients) ? row.recipients.length : 0;
      showFlash(`Email queued to ${count} recipient${count === 1 ? "" : "s"}.`);
      if (selected?.id === row.id) await refreshSelected(row.id);
    } catch (e) {
      const msg = e?.response?.data?.message || "Email failed.";
      setError(msg);
    } finally {
      setBusyRowId(null);
    }
  };

  const openCreate = () => {
    setModalEditRow(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalEditRow(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalEditRow(null);
  };

  const onModalSaved = async () => {
    const wasEdit = !!modalEditRow;
    const editedId = modalEditRow?.id;
    setModalOpen(false);
    setModalEditRow(null);
    showFlash(wasEdit ? "Report updated." : "Report created.");
    await load();
    if (wasEdit && editedId && selected?.id === editedId) {
      await refreshSelected(editedId);
    }
  };

  const openPopover = (row, kind, event) => {
    const btn = event.currentTarget;
    const rect = btn.getBoundingClientRect();
    const width = 340;
    let left = rect.right - width;
    if (left < 12) left = 12;
    const maxLeft = window.innerWidth - width - 12;
    if (left > maxLeft) left = maxLeft;

    let top = rect.bottom + 8;
    const estimatedHeight = 200;
    if (top + estimatedHeight > window.innerHeight - 12) {
      top = Math.max(12, rect.top - estimatedHeight - 8);
    }

    setPopover({ row, kind, top, left, width });
    if (kind === "delay") setDelayDays(1);
    setPanelAction(null);
  };

  const closePopover = () => setPopover(null);

  const popoverConfirmCancel = async () => {
    if (!popover) return;
    const row = popover.row;
    setPopover(null);
    await runAction(
      (r) => api.cancelScheduledExport(r.id),
      row,
      "Report cancelled.",
    );
  };

  const popoverConfirmDelete = async () => {
    if (!popover) return;
    const row = popover.row;
    setPopover(null);
    try {
      await api.deleteScheduledExport(row.id);
      showFlash("Report deleted.");
      if (selected?.id === row.id) {
        setSelected(null);
        setRuns([]);
      }
      await load();
    } catch {
      setError("Failed to delete report.");
    }
  };

  const popoverConfirmDelay = async () => {
    if (!popover) return;
    const row = popover.row;
    const days = Number(delayDays);
    if (!Number.isFinite(days) || days < 1) return;
    setPopover(null);
    await runAction(
      (r) => api.delayScheduledExport(r.id, days),
      row,
      `Next run delayed by ${days} day${days === 1 ? "" : "s"}.`,
    );
  };

  const beginPanel = (kind) => {
    setPopover(null);
    setPanelAction(kind);
    if (kind === "delay") setPanelDelayDays(1);
  };

  const cancelPanel = () => setPanelAction(null);

  const confirmPanelCancel = async () => {
    if (!selected) return;
    setPanelAction(null);
    await runAction(
      (r) => api.cancelScheduledExport(r.id),
      selected,
      "Report cancelled.",
    );
  };

  const confirmPanelDelete = async () => {
    if (!selected) return;
    setPanelAction(null);
    try {
      await api.deleteScheduledExport(selected.id);
      showFlash("Report deleted.");
      setSelected(null);
      setRuns([]);
      await load();
    } catch {
      setError("Failed to delete report.");
    }
  };

  const confirmPanelDelay = async () => {
    if (!selected) return;
    const days = Number(panelDelayDays);
    if (!Number.isFinite(days) || days < 1) return;
    setPanelAction(null);
    await runAction(
      (r) => api.delayScheduledExport(r.id, days),
      selected,
      `Next run delayed by ${days} day${days === 1 ? "" : "s"}.`,
    );
  };

  const toggleView = () =>
    setViewMode((m) => (m === "templates" ? "reports" : "templates"));

  return (
    <Layout
      title="Reports"
      subtitle="Build, preview, download, email, or schedule your reports"
    >
      <div className="r6-se-page">
        {/* =================== TOOLBAR =================== */}
        <div className="partner-leads-toolbar r6-se-toolbar">
          <button
            type="button"
            className="btn btn-primary"
            onClick={openCreate}
          >
            <i className="fas fa-plus" /> New Report
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/reports/templates/new")}
          >
            <i className="fas fa-layer-group" /> New Template
          </button>
          <button
            type="button"
            className={`btn btn-secondary ${
              viewMode === "templates" ? "is-active" : ""
            }`}
            onClick={toggleView}
            title={viewMode === "templates" ? "Back to reports" : "View templates"}
          >
            <i
              className={`fas ${
                viewMode === "templates" ? "fa-file-alt" : "fa-list"
              }`}
            />{" "}
            {viewMode === "templates" ? "View Reports" : "View Templates"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={load}
          >
            <i className="fas fa-sync" /> Refresh
          </button>
        </div>

        {flash && (
          <div className="r6-se-flash">
            <i className="fas fa-check-circle" /> {flash}
          </div>
        )}
        {error && (
          <div className="error-message" role="alert">
            <i className="fas fa-exclamation-circle" /> {error}
          </div>
        )}

        {/* =================== MAIN AREA =================== */}
        {viewMode === "templates" ? (
          <ReportTemplatesPanel
            onNew={() => navigate("/reports/templates/new")}
            onEdit={(id) => navigate(`/reports/templates/${id}`)}
            onView={(id) => setSelectedTemplateId(id)}
            refreshKey={templateRefreshKey}
          />
        ) : loading && rows.length === 0 ? (
          <div className="r6-se-muted">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty-state partner-empty-state">
            <i className="fas fa-file-alt" />
            <h3>No reports yet</h3>
            <p>
              Create a report to download it now, email it, or schedule it for
              recurring delivery.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={openCreate}
            >
              <i className="fas fa-plus" /> Create your first report
            </button>
          </div>
        ) : (
          <div className="partner-table-wrapper r6-se-table-wrapper">
            <table className="partner-leads-table">
              <thead>
                  <tr>
                    <th>Name</th>
                    <th>Template</th>
                    <th>Range</th>
                    <th>Format</th>
                    <th>Frequency</th>
                    <th>Values Mode</th>
                    <th>Recipients</th>
                    <th>Status</th>
                    <th>Next run</th>
                    <th>Last run</th>
                    <th className="actions-column">Actions</th>
                  </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isSelected = selected?.id === r.id;
                  const onDemand = isOnDemand(r);
                  const emailOk = canEmailNow(r);
                  return (
                    <tr
                      key={r.id}
                      onClick={() => openRow(r)}
                      className={isSelected ? "r6-se-tr-selected" : ""}
                      style={{ cursor: "pointer" }}
                    >
                      <td data-label="Name">
                        <span className="partner-lead-link">{r.name}</span>
                      </td>
                      <td data-label="Template">
                        {r.saved_view_name ||
                          r.saved_view?.name ||
                          (r.filters?.reportPreset &&
                          COLUMN_PRESETS[r.filters.reportPreset] ? (
                            COLUMN_PRESETS[r.filters.reportPreset].label
                          ) : r.saved_view_id ? (
                            `View #${r.saved_view_id}`
                          ) : (
                            <span className="text-muted">—</span>
                          ))}
                      </td>
                      <td data-label="Range">
                        {RANGE_LABEL[r.filters?.dateRange] || "All time"}
                      </td>
                      <td data-label="Format">
                        {(r.format || "").toUpperCase()}
                      </td>
                                             <td data-label="Values Mode">
                          {String(
                            r.filters?.multiValueColumnMode || "all",
                          ).toLowerCase() === "filtered" ? (
                            <span
                              className="status-badge active"
                              title="Products and DMS Providers trimmed to filtered values"
                            >
                              Filtered
                            </span>
                          ) : (
                            <span className="text-muted" title="All products and DMS shown">
                              All
                            </span>
                          )}
                        </td>
                      <td data-label="Frequency">
                        {onDemand ? (
                          <span className="status-badge new">On demand</span>
                        ) : (
                          r.frequency
                        )}
                      </td>
                      <td data-label="Recipients">
                        {(r.recipients || []).length
                          ? r.recipients.join(", ")
                          : "—"}
                      </td>
                      <td data-label="Status">
                        <span
                          className={`status-badge ${
                            STATUS_BADGE_CLASS[r.status] || "new"
                          }`}
                        >
                          {STATUS_LABEL[r.status] || r.status}
                        </span>
                      </td>
                      <td data-label="Next run">
                        {r.next_run_at
                          ? formatInUserTimezone(r.next_run_at)
                          : "—"}
                      </td>
                      <td data-label="Last run">
                        {r.last_run_at
                          ? formatInUserTimezone(r.last_run_at)
                          : "—"}
                      </td>
                      <td
                        data-label="Actions"
                        className="actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="partner-row-actions">
                          <button
                            type="button"
                            className="partner-icon-action success"
                            title="Download now"
                            disabled={busyRowId === r.id}
                            onClick={() => handleDownload(r)}
                          >
                            <i
                              className={`fas ${
                                busyRowId === r.id
                                  ? "fa-spinner fa-spin"
                                  : "fa-download"
                              }`}
                            />
                          </button>

                          <button
                            type="button"
                            className="partner-icon-action"
                            title={
                              emailOk
                                ? "Email now"
                                : "Add a recipient to enable email"
                            }
                            disabled={busyRowId === r.id || !emailOk}
                            onClick={() => handleEmailNow(r)}
                          >
                            <i className="fas fa-envelope" />
                          </button>

                          {!onDemand && r.status !== "cancelled" && (
                            <button
                              type="button"
                              className="partner-icon-action"
                              title="Run now"
                              onClick={() =>
                                runAction(
                                  (x) => api.runScheduledExportNow(x.id),
                                  r,
                                  "Run queued.",
                                )
                              }
                            >
                              <i className="fas fa-play" />
                            </button>
                          )}

                          {!onDemand && r.status === "active" && (
                            <button
                              type="button"
                              className="partner-icon-action"
                              title="Hold"
                              onClick={() =>
                                runAction(
                                  (x) => api.holdScheduledExport(x.id),
                                  r,
                                  "Report held.",
                                )
                              }
                            >
                              <i className="fas fa-pause" />
                            </button>
                          )}

                          {!onDemand && r.status === "held" && (
                            <button
                              type="button"
                              className="partner-icon-action"
                              title="Resume"
                              onClick={() =>
                                runAction(
                                  (x) => api.resumeScheduledExport(x.id),
                                  r,
                                  "Report resumed.",
                                )
                              }
                            >
                              <i className="fas fa-play" />
                            </button>
                          )}

                          {!onDemand && r.status !== "cancelled" && (
                            <button
                              type="button"
                              className="partner-icon-action"
                              title="Delay next run"
                              onClick={(e) => openPopover(r, "delay", e)}
                            >
                              <i className="fas fa-clock" />
                            </button>
                          )}

                          <button
                            type="button"
                            className="partner-icon-action"
                            title="Edit"
                            onClick={() => openEdit(r)}
                          >
                            <i className="fas fa-edit" />
                          </button>

                          {r.status === "cancelled" ? (
                            <button
                              type="button"
                              className="partner-icon-action danger"
                              title="Delete"
                              onClick={(e) => openPopover(r, "delete", e)}
                            >
                              <i className="fas fa-trash" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="partner-icon-action danger"
                              title="Cancel"
                              onClick={(e) => openPopover(r, "cancel", e)}
                            >
                              <i className="fas fa-ban" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* =================== REPORT DETAIL PANEL =================== */}
      {selected && (
        <div
          className="lead-panel-overlay"
          onMouseDown={() => setSelected(null)}
        >
          <aside
            className="lead-panel"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Report ${selected.name}`}
          >
            <div className="lead-panel-top">
              <div>
                <div className="lead-panel-eyebrow">
                  {isOnDemand(selected)
                    ? "On demand"
                    : STATUS_LABEL[selected.status] || selected.status}
                </div>
                <h2>{selected.name}</h2>
                <div className="lead-panel-subtitle">
                  Created{" "}
                  {selected.created_at
                    ? formatInUserTimezone(selected.created_at)
                    : "—"}
                </div>
              </div>
              <button
                type="button"
                className="lead-panel-close"
                onClick={() => setSelected(null)}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="lead-panel-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busyRowId === selected.id}
                onClick={() => handleDownload(selected)}
              >
                <i className="fas fa-download" /> Download
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                disabled={
                  busyRowId === selected.id || !canEmailNow(selected)
                }
                title={
                  canEmailNow(selected)
                    ? "Email now"
                    : "Add a recipient to enable email"
                }
                onClick={() => handleEmailNow(selected)}
              >
                <i className="fas fa-envelope" /> Email now
              </button>

              {!isOnDemand(selected) && selected.status === "active" && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    runAction(
                      (r) => api.holdScheduledExport(r.id),
                      selected,
                      "Report held.",
                    )
                  }
                >
                  <i className="fas fa-pause" /> Hold
                </button>
              )}

              {!isOnDemand(selected) && selected.status === "held" && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    runAction(
                      (r) => api.resumeScheduledExport(r.id),
                      selected,
                      "Report resumed.",
                    )
                  }
                >
                  <i className="fas fa-play" /> Resume
                </button>
              )}

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => openEdit(selected)}
              >
                <i className="fas fa-edit" /> Edit
              </button>

              {selected.status === "cancelled" ? (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => beginPanel("delete")}
                >
                  <i className="fas fa-trash" /> Delete
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => beginPanel("cancel")}
                >
                  <i className="fas fa-ban" /> Cancel
                </button>
              )}
            </div>

            {isOnDemand(selected) && !canEmailNow(selected) && (
              <section className="lead-panel-section">
                <div className="r6-se-panel-confirm">
                  <div className="r6-se-panel-confirm-msg">
                    <i className="fas fa-info-circle" /> This is an on-demand
                    report with no recipients. Email is disabled — add at least
                    one recipient via Edit to enable it.
                  </div>
                </div>
              </section>
            )}

            {panelAction === "cancel" && (
              <section className="lead-panel-section">
                <div className="r6-se-panel-confirm">
                  <div className="r6-se-panel-confirm-msg">
                    Cancel <strong>{selected.name}</strong>? The schedule will
                    stop running but stay in your list.
                  </div>
                  <div className="r6-se-panel-confirm-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={cancelPanel}
                    >
                      Keep report
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={confirmPanelCancel}
                    >
                      <i className="fas fa-ban" /> Confirm cancel
                    </button>
                  </div>
                </div>
              </section>
            )}

            {panelAction === "delete" && (
              <section className="lead-panel-section">
                <div className="r6-se-panel-confirm r6-se-panel-confirm-danger">
                  <div className="r6-se-panel-confirm-msg">
                    Permanently delete <strong>{selected.name}</strong>? Its
                    entire run history will be removed.
                  </div>
                  <div className="r6-se-panel-confirm-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={cancelPanel}
                    >
                      Keep report
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={confirmPanelDelete}
                    >
                      <i className="fas fa-trash" /> Delete permanently
                    </button>
                  </div>
                </div>
              </section>
            )}

            {panelAction === "delay" && (
              <section className="lead-panel-section">
                <div className="r6-se-panel-confirm">
                  <div className="r6-se-panel-confirm-msg">
                    Delay the next run of <strong>{selected.name}</strong> by:
                  </div>
                  <label className="r6-se-panel-delay-label">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={panelDelayDays}
                      onChange={(e) => setPanelDelayDays(e.target.value)}
                      className="form-control r6-se-delay-input"
                    />
                    <span>
                      day{Number(panelDelayDays) === 1 ? "" : "s"}
                    </span>
                  </label>
                  <div className="r6-se-panel-confirm-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={cancelPanel}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={confirmPanelDelay}
                    >
                      Apply Delay
                    </button>
                  </div>
                </div>
              </section>
            )}

            <section className="lead-panel-section">
              <div className="lead-panel-section-title">Details</div>
              <div className="lead-panel-grid">
                <div className="lead-panel-detail">
                  <span className="lead-panel-detail-label">Template</span>
                  <span className="lead-panel-detail-value">
                    {selected.saved_view_name ||
                      selected.saved_view?.name ||
                      "—"}
                  </span>
                </div>
                <div className="lead-panel-detail">
                  <span className="lead-panel-detail-label">Range</span>
                  <span className="lead-panel-detail-value">
                    {RANGE_LABEL[selected.filters?.dateRange] || "All time"}
                    {selected.filters?.dateRange === "custom" &&
                    selected.filters?.dateFrom &&
                    selected.filters?.dateTo
                      ? ` (${selected.filters.dateFrom} → ${selected.filters.dateTo})`
                      : ""}
                  </span>
                </div>
                <div className="lead-panel-detail">
                  <span className="lead-panel-detail-label">Based on</span>
                  <span className="lead-panel-detail-value">
                    {selected.filters?.dateField || "created"}
                  </span>
                </div>
                <div className="lead-panel-detail">
                  <span className="lead-panel-detail-label">Format</span>
                  <span className="lead-panel-detail-value">
                    {(selected.format || "").toUpperCase()}
                  </span>
                </div>
                <div className="lead-panel-detail">
                  <span className="lead-panel-detail-label">Frequency</span>
                  <span className="lead-panel-detail-value">
                    {isOnDemand(selected) ? "On demand" : selected.frequency}
                  </span>
                </div>
                {!isOnDemand(selected) && (
                  <div className="lead-panel-detail">
                    <span className="lead-panel-detail-label">Time</span>
                    <span className="lead-panel-detail-value">
                      {selected.time_of_day} {selected.timezone}
                    </span>
                  </div>
                )}
                <div className="lead-panel-detail">
                  <span className="lead-panel-detail-label">Next run</span>
                  <span className="lead-panel-detail-value">
                    {selected.next_run_at
                      ? formatInUserTimezone(selected.next_run_at)
                      : "—"}
                  </span>
                </div>
                <div className="lead-panel-detail">
                  <span className="lead-panel-detail-label">Last run</span>
                  <span className="lead-panel-detail-value">
                    {selected.last_run_at
                      ? formatInUserTimezone(selected.last_run_at)
                      : "—"}
                  </span>
                </div>
                {!isOnDemand(selected) && (
                  <div className="lead-panel-detail">
                    <span className="lead-panel-detail-label">Reminder</span>
                    <span className="lead-panel-detail-value">
                      {selected.reminder_enabled ? "On" : "Off"}
                    </span>
                  </div>
                )}
              </div>
            </section>

            <section className="lead-panel-section">
              <div className="lead-panel-section-title">Recipients</div>
              {(selected.recipients || []).length ? (
                <div className="lead-panel-tags">
                  {selected.recipients.map((email, i) => (
                    <span className="lead-panel-tag" key={`${email}-${i}`}>
                      {email}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="lead-panel-empty">No recipients</div>
              )}
            </section>

            <section className="lead-panel-section">
              <div className="lead-panel-section-title">Last 10 runs</div>
              <table className="r6-se-runs-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Rows</th>
                    <th>Started</th>
                    <th>Finished</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.length === 0 && (
                    <tr>
                      <td colSpan="4" className="r6-se-muted">
                        No runs yet.
                      </td>
                    </tr>
                  )}
                  {runs.map((run) => (
                    <tr key={run.id}>
                      <td>
                        <span
                          className={`r6-run-status status-${run.status}`}
                        >
                          {run.status}
                        </span>
                      </td>
                      <td>{run.row_count || 0}</td>
                      <td>
                        {run.started_at
                          ? formatInUserTimezone(run.started_at)
                          : "—"}
                      </td>
                      <td>
                        {run.finished_at
                          ? formatInUserTimezone(run.finished_at)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </aside>
        </div>
      )}

      {/* =================== TEMPLATE DETAIL PANEL =================== */}
      {selectedTemplateId && (
        <ReportTemplateViewPanel
          templateId={selectedTemplateId}
          onClose={() => setSelectedTemplateId(null)}
          onEdit={(id) => navigate(`/reports/templates/${id}`)}
          onDeleted={() => {
            setSelectedTemplateId(null);
            setTemplateRefreshKey((k) => k + 1);
            showFlash("Template deleted.");
          }}
        />
      )}

      {/* =================== POPOVERS =================== */}
      {popover &&
        createPortal(
          <>
            <div
              className="r6-se-popover-backdrop"
              onMouseDown={() => setPopover(null)}
            />
            <div
              ref={popoverRef}
              className="r6-se-popover"
              style={{
                position: "fixed",
                top: `${popover.top}px`,
                left: `${popover.left}px`,
                width: `${popover.width}px`,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {popover.kind === "cancel" && (
                <>
                  <div className="r6-se-popover-head">
                    <span className="r6-se-popover-icon warning">
                      <i className="fas fa-exclamation-triangle" />
                    </span>
                    <div className="r6-se-popover-title">
                      Cancel this report?
                    </div>
                  </div>
                  <div className="r6-se-popover-body">
                    <strong>{popover.row.name}</strong> will stop running but
                    stay in your list. You can reopen it later.
                  </div>
                  <div className="r6-se-popover-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={closePopover}
                    >
                      Keep
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={popoverConfirmCancel}
                    >
                      <i className="fas fa-ban" /> Confirm cancel
                    </button>
                  </div>
                </>
              )}

              {popover.kind === "delete" && (
                <>
                  <div className="r6-se-popover-head">
                    <span className="r6-se-popover-icon danger">
                      <i className="fas fa-trash" />
                    </span>
                    <div className="r6-se-popover-title">
                      Delete permanently?
                    </div>
                  </div>
                  <div className="r6-se-popover-body">
                    <strong>{popover.row.name}</strong> and its run history
                    will be removed. This cannot be undone.
                  </div>
                  <div className="r6-se-popover-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={closePopover}
                    >
                      Keep
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={popoverConfirmDelete}
                    >
                      <i className="fas fa-trash" /> Delete
                    </button>
                  </div>
                </>
              )}

              {popover.kind === "delay" && (
                <>
                  <div className="r6-se-popover-head">
                    <span className="r6-se-popover-icon info">
                      <i className="fas fa-clock" />
                    </span>
                    <div className="r6-se-popover-title">Delay next run</div>
                  </div>
                  <div className="r6-se-popover-body">
                    Push <strong>{popover.row.name}</strong>'s next run
                    forward.
                  </div>
                  <div className="r6-se-popover-field">
                    <label>Delay by</label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={delayDays}
                      onChange={(e) => setDelayDays(e.target.value)}
                      className="form-control r6-se-popover-input"
                    />
                    <span>day{Number(delayDays) === 1 ? "" : "s"}</span>
                  </div>
                  <div className="r6-se-popover-hint">
                    New next run:{" "}
                    <strong>
                      {(() => {
                        const base = popover.row.next_run_at
                          ? new Date(popover.row.next_run_at)
                          : new Date();
                        const d = new Date(base.getTime());
                        const n = Number(delayDays) || 0;
                        d.setUTCDate(d.getUTCDate() + n);
                        return formatInUserTimezone(d);
                      })()}
                    </strong>
                  </div>
                  <div className="r6-se-popover-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={closePopover}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={popoverConfirmDelay}
                    >
                      <i className="fas fa-clock" /> Apply Delay
                    </button>
                  </div>
                </>
              )}
            </div>
          </>,
          document.body,
        )}

      <ScheduleExportModal
        open={modalOpen}
        currentFilters={{}}
        editRow={modalEditRow}
        onClose={closeModal}
        onCreated={onModalSaved}
      />
    </Layout>
  );
}